import React from "react";

import { FieldDependencies, FieldTypeConfig } from "@/domains/datablocks/types";
import { FileRepositoryUploadProps } from "@/components_v2/FormFields/Upload/types";
import {
  UPLOAD_CONTEXT_MAPPER,
  useDataBlockContextHandlers,
} from "../hooks/useDataBlockContextHandlers";
import {
  getEditorComponentForContentType,
  getEditorPropsForContentType,
} from "./contentTypeConfig";
import {
  HookFormDatePicker,
  HookFormDateRangePicker,
  HookFormFileRepositoryUpload,
  HookFormInput,
  HookFormRichTextInput,
  HookFormSlider,
  HookFormTextArea,
} from "@/components_v2/FormFields/ReactHookForm/fields";
import { HookFormComponentProps } from "@/components_v2/FormFields/ReactHookForm/types";
import {
  DataFieldResponse,
  DisplayType,
  FieldType,
} from "@/redux/datablocks/types";
import { BaseData } from "@/redux/types";

export const FileRepositoryUploadWithHook = <T extends BaseData>(
  props: HookFormComponentProps<FileRepositoryUploadProps<T>>,
) => {
  const contextHandlers = useDataBlockContextHandlers(UPLOAD_CONTEXT_MAPPER);
  return <HookFormFileRepositoryUpload {...contextHandlers} {...props} />;
};

const getFieldComponent = (
  field: DataFieldResponse,
  dependencies: FieldDependencies,
): Record<string, any> => {
  switch (field.type) {
    case FieldType.TEXT: {
      let component;
      if (field.displayType === DisplayType.INPUT) {
        component = HookFormInput;
      } else if (field.displayType === DisplayType.TEXT_AREA) {
        component = HookFormTextArea;
      } else if (field.displayType === DisplayType.RICH_TEXT) {
        component = HookFormRichTextInput;
        return {
          component,
          getProps: () => ({ media: { useMediaUpload: true } }),
        };
      }
      return {
        component,
      };
    }
    case FieldType.NUMBER:
      return {
        component: HookFormInput,
        getProps: () => ({
          type: "number",
        }),
      };
    case FieldType.DATE: {
      if (field.displayType === DisplayType.DATE_RANGE_PICKER) {
        return {
          component: HookFormDateRangePicker,
          getProps: () => ({
            allowEmpty: [true, true],
          }),
        };
      }
      return {
        component: HookFormDatePicker,
      };
    }
    case FieldType.SELECT: {
      if (field.displayType === DisplayType.SLIDER) {
        return {
          component: HookFormSlider,
          getProps: (field: DataFieldResponse) => {
            const options = field?.options || [];
            return {
              options: options.map((option) => ({
                value: option.id,
                label: option.label,
              })),
            };
          },
        };
      }

      return {
        isComponentGetter: true,
        component: (field: DataFieldResponse) =>
          getEditorComponentForContentType(field),
        getProps: (field: DataFieldResponse) => ({
          allowClear: true,
          multiple: field.multiple,
          ...(field.displayType === DisplayType.DROPDOWN_WITH_SCALE
            ? {
                helpText: field.sliderHelpText,
                customInputOptions: {
                  show: field.allowsCustomOptions,
                },
              }
            : {}),
          ...getEditorPropsForContentType(field, dependencies),
        }),
      };
    }
    default:
      return {
        component: HookFormInput,
      };
  }
};

/**
 * Retrieves the form shared for the specific field type along with its properties
 */
export const getFieldTypeComponent = (
  field: DataFieldResponse,
  dependencies: FieldDependencies,
): {
  component: React.ComponentType;
  props: ReturnType<NonNullable<FieldTypeConfig["getProps"]>>;
} => {
  const fieldComponent = getFieldComponent(field, dependencies);

  if (!fieldComponent) {
    throw new Error(`Can't find component for field of type ${field.type}`);
  }
  let component = fieldComponent.component as React.ComponentType;
  if (fieldComponent?.isComponentGetter) {
    const componentGetter = fieldComponent.component as (
      field: DataFieldResponse,
    ) => React.ComponentType;
    component = componentGetter(field);
  }

  return {
    component,
    props: fieldComponent.getProps?.(field) ?? {},
  };
};
